import React, { useMemo, useState } from 'react';
import { Card, DatePicker, Select, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useExportSystemOperationRecordsMutation, useGetSystemOperationRecordsQuery } from '../../features/systemOps/systemOperationsService';
import { SystemOperationRecordResponse, SystemOperationRecordTypeEnum, SystemOperationTypeOptions } from '../../models/SystemOpsModels';
import RoundedButton from '../../components/RoundedButton';
import RoundedButtonWithBorder from '../../components/RoundedButtonWithBorder';
import { Utility } from '../../utilities/Utility';
import { EmDash } from '../../global/constants';
import { ColumnsType } from 'antd/lib/table';
import { useExportOrganizationFileMutation } from '../../features/organization/organizationService';
import { HospiceOrganizationResponse } from '../../models/OrderSetModels';
import { TeamResponse } from '../../models/AppointmentModels';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SystemOperationsPage = () => {
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedType, setSelectedType] = useState<SystemOperationRecordTypeEnum>(SystemOperationRecordTypeEnum.AllergyImportIssue);
    const [dateRange, setDateRange] = useState<any>(null);

    // multiple rows can be exporting at the same time
    const [exportingCr8358Ids, setExportingCr8358Ids] = useState<string[]>([]);

    const query = useMemo(() => {
        return {
            pageNumber,
            pageSize,
            type: selectedType,
            occuredAtFrom: dateRange?.[0]
                ? dayjs(dateRange[0]).startOf('day').toISOString()
                : null,
            occuredAtTo: dateRange?.[1]
                ? dayjs(dateRange[1]).endOf('day').toISOString()
                : null,
        };
    }, [pageNumber, pageSize, selectedType, dateRange]);

    const {
        data: systemOperationRecords,
        isFetching: isFetchingSystemOperationRecords,
        refetch: refetchSystemOperationRecords
    } = useGetSystemOperationRecordsQuery(query);

    const [systemOperationRecordsExcelDataMutation, { isLoading: isExporting }] =
        useExportSystemOperationRecordsMutation();

    const [exportOrganizationFileMutation] = useExportOrganizationFileMutation();

    const exportRecords = async () => {
        const response = await systemOperationRecordsExcelDataMutation(query);

        if (typeof response === 'object' && 'data' in response) {
            Utility.downloadBase64File(
                response.data.base64Content,
                response.data.fileName,
                response.data.contentType
            );
        }
    };

    const clearFilters = () => {
        setDateRange(null);
        setPageNumber(1);
    };

    const exportCr8358 = async (record: SystemOperationRecordResponse) => {
        const organizationId = record.organizationDetails?.id;
        const fileName = record.details;

        if (!organizationId || !fileName) return;

        const rowId = record.id;

        setExportingCr8358Ids((prev) =>
            prev.includes(rowId) ? prev : [...prev, rowId]
        );

        try {
            const response = await exportOrganizationFileMutation({
                organizationId,
                fileName,
            });

            if (typeof response === 'object' && 'data' in response) {
                Utility.downloadBase64File(
                    response.data.base64Content,
                    response.data.fileName,
                    response.data.contentType,
                );
            }
        } finally {
            setExportingCr8358Ids((prev) => prev.filter((id) => id !== rowId));
        }
    };

    const getDetails = (record: SystemOperationRecordResponse) => {
        if (record.type === SystemOperationRecordTypeEnum.AllergyImportIssue) {
            return <Text>{record.details || EmDash}</Text>;
        }

        if (record.type === SystemOperationRecordTypeEnum.CR8358) {
            const organizationId = record.organizationDetails?.id;
            const fileName = record.details;
            const isCurrentRowExporting = exportingCr8358Ids.includes(record.id);

            return (
                <RoundedButton
                    className={'fw-semibold bg-light-blue text-white fw-semibold'}
                    disabled={!organizationId || !fileName}
                    onClick={() => exportCr8358(record)}
                >
                    {isCurrentRowExporting ? 'Exporting...' : 'Export'}
                </RoundedButton>
            );
        }

        return <Text>{record.details || EmDash}</Text>;
    };

    const baseColumns: ColumnsType<SystemOperationRecordResponse> = [
        {
            title: 'Occurred At',
            dataIndex: 'occuredAt',
            key: 'occuredAt',
            render: (value: string) => Utility.getFormattedDateTime2(value),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (value: number) => {
                const found = SystemOperationTypeOptions.find((x) => x.value === value);
                return found?.label || value;
            },
        },
    ];

    const allergyImportIssueColumns: ColumnsType<SystemOperationRecordResponse> = [
        ...baseColumns,
        {
            title: 'HCRx Patient ID#',
            dataIndex: 'patientHcrxCodes',
            key: 'patientHcrxCodes',
            render: (value: string[]) => value?.join(', ') || EmDash,
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            render: (_, record) => getDetails(record),
        },
    ];

    const cr8358Columns: ColumnsType<SystemOperationRecordResponse> = [
        ...baseColumns,
        {
            title: 'Organization',
            dataIndex: 'organizationDetails',
            key: 'organizationDetails',
            render: (value: HospiceOrganizationResponse) => value?.hospiceName || EmDash,
        },
        {
            title: 'Team',
            dataIndex: 'teamDetails',
            key: 'teamDetails',
            render: (value: TeamResponse | null | undefined) => value?.name || EmDash,
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            render: (_, record) => getDetails(record),
        },
    ];

    const columns =
        selectedType === SystemOperationRecordTypeEnum.CR8358
            ? cr8358Columns
            : allergyImportIssueColumns;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Title level={2} style={{ marginBottom: 0 }}>
                    System Operations
                </Title>
            </div>

            <Card>
                <div className='d-flex flex-row justify-content-between w-100 mb-2'>
                    <div className='d-flex flex-row gap-2'>
                        <Select
                            allowClear={false}
                            placeholder="Select type"
                            style={{ width: 220 }}
                            options={SystemOperationTypeOptions}
                            value={selectedType}
                            onChange={(value) => {
                                setSelectedType(value ?? null);
                                setPageNumber(1);
                            }}
                        />

                        <RangePicker
                            className='rounded-2'
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPageNumber(1);
                            }}
                        />

                        <RoundedButton
                            className={'bg-light-blue text-white fw-semibold'}
                            onClick={refetchSystemOperationRecords}
                        >
                            Reload
                        </RoundedButton>

                        <RoundedButtonWithBorder
                            className={'fw-semibold'}
                            onClick={clearFilters}
                        >
                            Clear
                        </RoundedButtonWithBorder>
                    </div>

                    {selectedType === SystemOperationRecordTypeEnum.AllergyImportIssue && (
                        <RoundedButton
                            className={'fw-semibold bg-light-blue text-white fw-semibold'}
                            onClick={exportRecords}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </RoundedButton>
                    )}
                </div>

                <Table
                    rowKey="id"
                    loading={isFetchingSystemOperationRecords}
                    columns={columns}
                    dataSource={systemOperationRecords?.data || []}
                    pagination={{
                        current: pageNumber,
                        pageSize,
                        total: systemOperationRecords?.totalCount || 0,
                        showSizeChanger: true,
                        onChange: (page, size) => {
                            setPageNumber(page);
                            setPageSize(size || 10);
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export default SystemOperationsPage;
